// /party: a party, and nothing else. Start one on this device and share its link, join one with its code, or put it on a
// big screen (Cast to your TV, or this screen). Friends asking you to their game come first. The home page leads with
// today's Daily Gig and a party; this is the whole of the party.
import { $, SB, TRACK, avatar, deviceId, esc, icon, t, track } from '../web/common.ts';
import type { IconName } from '../web/common.ts';
import { SBACCT } from '../web/account.ts';
import { FRIENDS, friendSeat } from '../web/friends.ts';
import { mountNav } from '../web/nav.ts';
import { keepFriendCount } from '../web/nav-friends.ts';
import { CHROME, IOS, MAC, askTv, castContext, castDevices, castOn, castPermission, loadCastSdk, onCastChange, wait } from '../web/cast-send.ts';

document.title = t('Party') + ' · SongBuzz';
document.querySelectorAll<HTMLElement>('[data-t]').forEach((el) => { el.textContent = t(el.dataset.t!); });
mountNav('party'); keepFriendCount();
document.querySelectorAll<HTMLElement>('.ic-slot').forEach((el) => { el.outerHTML = icon(el.dataset.icon as IconName); });
document.querySelectorAll<HTMLElement>('[data-choice]').forEach((a) => a.addEventListener('click', () => track('party_choice', { choice: a.dataset.choice })));
// a party on this device needs cloud rooms: on the LAN this server is the show, so it starts on this screen
if (SB.mode !== 'cloud') { const h = $<HTMLAnchorElement>('#hostBtn'); h.href = '/tv'; h.querySelector('small')!.textContent = t('On a TV or a computer: this screen plays the songs, everyone answers on their phone.'); }
// a phone isn't the big screen: it says where to open it instead
const onPhone = TRACK.dev === 'phone';
if (onPhone) $('#screenBtn').hidden = true;
$('#moreP').innerHTML = onPhone ? t('Got a TV or a computer? Open {addr} on it to show the game there.', { addr: `<code>${esc(location.host)}/tv</code>` })
  : t('On a TV without a keyboard? Open {addr} in its browser.', { addr: `<code>${esc(location.host)}/tv</code>` });

// ---------------------------------------------------------------- joining with a code
const codeIn = $<HTMLInputElement>('#code');
codeIn.addEventListener('input', () => { codeIn.value = codeIn.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 4); });
$('#codeForm').onsubmit = (e) => {
  e.preventDefault();
  const v = codeIn.value.toUpperCase().replace(/[^A-Z]/g, '');
  if (SB.mode === 'cloud' && v.length !== 4) return codeIn.focus();
  track('party_choice', { choice: 'join' });
  location.href = '/j' + (v ? '?r=' + v : '');
};
if (SB.mode !== 'cloud') { $('#codeLabel').textContent = t('On the same Wi-Fi as the game?'); codeIn.style.display = 'none'; $('#joinHint').textContent = t("Or scan the QR code on the game's screen."); }

// ---------------------------------------------------------------- friends asking you to their game (the last half hour's)
async function asks() {
  if (!SBACCT || !(await SBACCT.account())) return;
  const r = await FRIENDS.invites(), list = r ? r.invites : [];
  const box = $('#asks');
  box.hidden = !list.length;
  box.innerHTML = list.map((v) => `<div class="ask">${avatar(friendSeat(v.id, v.look), 'grin')}<span><b>${esc(t('{name} asked you to their game', { name: v.name || t('A friend') }))}</b><small>${esc(t('Room {code}', { code: v.room }))}</small></span><a class="btn go" href="/j?r=${esc(v.room)}" data-choice="invite">${esc(t('Join'))}</a></div>`).join('');
}
asks();

// ---------------------------------------------------------------- Cast to your TV (cast-send.ts)
// The button always tries to cast; when it can't, a panel says what went wrong and what to do. Not on the TV itself, or
// on the LAN server (the TV is the server there).
const castCta = $('#castCta'), castBtn = $('#castBtn'), castHelpEl = $('#castHelp');
let castState: CastStatus = 'idle', castBusy = false;
const tvAddr = () => `<code>${esc(location.host)}/tv</code>`;

// the line under the button
const CAST_LINE = {
  idle: () => t('Your TV and this device need to be on the same Wi-Fi'),
  searching: () => t('Looking for your TV… if Chrome asks, choose Allow'),
  ready: () => t('TV found on your Wi-Fi'),
  connecting: () => t('Opening the show on the TV…'),
  none: () => t('No TV found'),
  blocked: () => t('Chrome is blocking the search for your TV'),
  error: () => t("The TV didn't answer"),
  unsupported: () => (IOS ? t("Can't cast from iPhone or iPad") : t('Casting needs Google Chrome')),
};
type CastStatus = keyof typeof CAST_LINE;
function setCast(st: CastStatus) {
  castState = st;
  castCta.dataset.cast = st;
  castBtn.setAttribute('aria-busy', String(st === 'searching' || st === 'connecting'));
  const how = ['none', 'blocked', 'error', 'unsupported'].includes(st) && castHelpEl.hidden ? ` <button class="why" type="button">${t('How?')}</button>` : '';
  $('#castState').innerHTML = `<span>${esc(CAST_LINE[st]())}${how}</span>`;
  if (['ready', 'searching', 'connecting'].includes(st)) closeCastHelp();
}

// the panel: what went wrong, what to do, and a way to try again
function castPanel(kind: CastStatus) {
  const alt = `<span class="alt">${t('No Chromecast? Open {addr} in your TV\'s browser instead.', { addr: tvAddr() })}</span>`;
  const retry = `<button class="btn go" type="button" data-retry>${t('Try again')}</button>`;
  // on a Mac, Chrome finds no Chromecast at all until macOS lets it see the local network (macOS 15 and later)
  if (kind === 'none') return `<h3>${t('No TV found')}</h3><ol><li>${t('Turn on your Chromecast or Google TV.')}</li><li>${t('Connect this device to the same Wi-Fi as the TV.')}</li><li>${t('If Chrome asks to look for devices, choose Allow.')}</li>${MAC ? `<li>${t('On a Mac: System Settings → Privacy & Security → Local Network, turn on Google Chrome, then restart Chrome.')}</li>` : ''}</ol><p>${t("Still nothing? Open Chrome's menu (⋮) → Cast, save and share → Cast. If your TV isn't listed there either, Chrome can't see it on this network.")}</p><div class="acts">${retry}${alt}</div>`;
  if (kind === 'blocked') return `<h3>${t('Chrome needs your OK')}</h3><p>${t('To find your TV, Chrome must be allowed to look for devices on your network. Click the icon at the left of the address bar, allow "Apps on device" (or "Local network") for this site, then try again.')}</p><div class="acts">${retry}${alt}</div>`;
  if (kind === 'error') return `<h3>${t("The TV didn't answer")}</h3><p>${t('Make sure the TV is still on and on the same Wi-Fi, then try again.')}</p><div class="acts">${retry}${alt}</div>`;
  if (IOS) return `<h3>${t("Can't cast from iPhone or iPad")}</h3><p>${t('iPhone and iPad can\'t cast a web page. Cast from Chrome on an Android phone or a computer, or open {addr} in your TV\'s browser and join with this device.', { addr: tvAddr() })}</p>`;
  return `<h3>${t('Casting needs Google Chrome')}</h3><p>${t('This browser can\'t cast. Open this page in Google Chrome, or open {addr} in your TV\'s browser and join with this device.', { addr: tvAddr() })}</p>`;
}
function openCastHelp(kind: CastStatus) {
  castHelpEl.innerHTML = `<button class="x" type="button" data-close aria-label="${esc(t('Close'))}">×</button>` + castPanel(kind);
  castHelpEl.hidden = false;
  setCast(castState); // drops the "How?" link
  track('cast_help', { state: kind });
}
function closeCastHelp() { if (!castHelpEl.hidden) { castHelpEl.hidden = true; castHelpEl.innerHTML = ''; } }
function castFail(kind: CastStatus, why?: string) {
  setCast(kind);
  openCastHelp(kind);
  track('cast_result', { result: kind, why: why || '' });
}

async function castNow() {
  if (castState === 'unsupported') return openCastHelp('unsupported');
  if (castBusy) return;
  castBusy = true;
  const tapAt = Date.now();
  track('cast_tap', { state: castState });
  try {
    if (!castContext()) {
      setCast('searching');
      try { await loadCastSdk(); } catch (e) { return castFail(/unavailable/.test((e as Error).message) ? 'unsupported' : 'error', 'sdk_' + (e as Error).message); }
    }
    // devices show up a moment after the library starts looking (a cold start takes a few seconds), but a Chrome that
    // keeps the network to itself until this page may look shows none at all: so after a short wait Chrome's own Cast
    // list opens anyway, where it can ask for that or show the TVs, and our help only comes up if Chrome says no TV
    for (let i = 0; i < 12 && !castDevices(); i++) { if (castState !== 'searching') setCast('searching'); await wait(250); }
    const seen = castDevices(), why = () => `seen_${seen}·state_${castContext() ? castContext()!.getCastState() : 'nosdk'}·${MAC ? 'mac' : TRACK.dev}`;
    // Chrome only opens its list straight after a tap: if loading took a while, ask for one more tap
    if (Date.now() - tapAt > 4500) { setCast(seen ? 'ready' : 'idle'); $('#castState').innerHTML = `<span>${esc(seen ? t('TV found: tap Cast to your TV again to pick it') : t('Ready: tap Cast to your TV again'))}</span>`; return; }
    if (seen) setCast('ready');
    try { await castContext()!.requestSession(); } catch (e) {
      const code = String((e && (e as { code?: string }).code) || e);
      if (code === 'cancel') return setCast(castDevices() ? 'ready' : 'idle');
      const p = await castPermission();
      return castFail(code === 'receiver_unavailable' ? (p === 'denied' ? 'blocked' : 'none') : 'error', `${code}·perm_${p}·${why()}`);
    }
    track('cast_start', {});
    setCast('connecting');
    const session = castContext()!.getCurrentSession()!;
    // the TV needs a moment to open a room: ask until it answers
    const room = await askTv(session, { t: 'room', pid: deviceId() }, (d) => (typeof d.room === 'string' ? d.room : null));
    if (!room) return castFail('error', 'no_room');
    track('cast_result', { result: 'started' });
    location.href = '/j?r=' + room; // this device plays (and runs the show) from here
  } finally { castBusy = false; }
}

// where this device can't cast (an iPhone or iPad, a TV, the LAN server), the key isn't there: the other ways are
if (!castOn) castCta.hidden = true;
if (castOn) {
  castBtn.onclick = castNow;
  castCta.addEventListener('click', (e) => {
    if ((e.target as Element).closest('.why')) openCastHelp(castState);
    else if ((e.target as Element).closest('[data-close]')) { closeCastHelp(); setCast(castState); castBtn.focus(); }
    else if ((e.target as Element).closest('[data-retry]')) { closeCastHelp(); castNow(); }
  });
  // keep the line honest while nothing is going on
  onCastChange(() => { if (!castBusy && ['idle', 'ready', 'none'].includes(castState)) setCast(castDevices() ? 'ready' : castState === 'none' ? 'none' : 'idle'); });
  if (!CHROME) setCast('unsupported');
  else {
    setCast('idle');
    // already allowed on an earlier visit: start looking now, so the line can say whether a TV is there
    castPermission().then((p) => {
      if (p === 'denied') return setCast('blocked');
      if (p === 'granted') loadCastSdk().then(() => { if (castState === 'idle') setCast(castDevices() ? 'ready' : 'idle'); }).catch(() => {});
    });
  }
}

